import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { quoteService } from '../services/quoteService';

const fmt = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return `£${parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
};

const STATUS_COLORS = {
  'Created':             '#1d4ed8',
  'Waiting on customer': '#b45309',
  'Accepted':            '#15803d',
  'Rejected':            '#b91c1c',
};

const PrintQuote = () => {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    quoteService.getQuote(id)
      .then(res => setQuote(res.data))
      .catch(() => setError('Quote not found.'));
  }, [id]);

  useEffect(() => {
    if (quote) {
      document.title = `Quote #${quote.quote_number}`;
    }
  }, [quote]);

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'sans-serif' }}>
        <p style={{ color: '#b91c1c' }}>{error}</p>
      </div>
    );
  }

  if (!quote) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'sans-serif', color: '#6b7280' }}>
        Loading…
      </div>
    );
  }

  const job = quote.job;
  const site = job?.site;
  const customer = site?.customer;
  const items = quote.items || [];
  const quoteTotal = items.reduce((sum, item) => sum + Math.abs(parseFloat(item.value || 0)), 0);
  const grossProfit = items.reduce((sum, item) => sum + parseFloat(item.value || 0), 0);
  const status = quote.status || 'Created';
  const statusColor = STATUS_COLORS[status] || '#1d4ed8';

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
          @page { margin: 1.5cm; size: A4 portrait; }
        }
        body { font-family: 'Segoe UI', Arial, sans-serif; background: #f3f4f6; margin: 0; }
      `}</style>

      {/* Toolbar – hidden on print */}
      <div className="no-print" style={{ background: '#1e293b', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ color: '#94a3b8', fontSize: '14px', flex: 1 }}>Quote #{quote.quote_number}</span>
        <button
          onClick={() => window.print()}
          style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 20px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
        >
          🖨 Print
        </button>
        <button
          onClick={() => window.close()}
          style={{ background: '#374151', color: '#d1d5db', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '14px', cursor: 'pointer' }}
        >
          Close
        </button>
      </div>

      {/* Quote page */}
      <div style={{ maxWidth: '800px', margin: '32px auto 64px', background: '#fff', borderRadius: '8px', boxShadow: '0 4px 24px rgba(0,0,0,0.1)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ background: '#1e293b', padding: '40px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ color: '#fff', fontSize: '32px', fontWeight: 800, letterSpacing: '-0.5px' }}>QUOTE</div>
            <div style={{ color: '#94a3b8', fontSize: '15px', marginTop: '4px' }}>#{quote.quote_number}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ display: 'inline-block', background: statusColor, color: '#fff', borderRadius: '20px', padding: '4px 14px', fontSize: '13px', fontWeight: 700, letterSpacing: '0.3px' }}>
              {status}
            </span>
            <div style={{ color: '#94a3b8', fontSize: '13px', marginTop: '8px' }}>
              Issued: {fmtDate(quote.created_at)}
            </div>
          </div>
        </div>

        <div style={{ padding: '40px 48px' }}>

          {/* Addresses */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginBottom: '40px' }}>
            {/* Customer */}
            <div>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Customer</div>
              {customer ? (
                <>
                  <div style={{ fontWeight: 700, fontSize: '16px', color: '#111827' }}>{customer.name}</div>
                  {customer.street_address && <div style={{ color: '#374151', fontSize: '14px', marginTop: '4px' }}>{customer.street_address}</div>}
                  {customer.town && <div style={{ color: '#374151', fontSize: '14px' }}>{customer.town}</div>}
                  {customer.county && <div style={{ color: '#374151', fontSize: '14px' }}>{customer.county}</div>}
                  {customer.postcode && <div style={{ color: '#374151', fontSize: '14px' }}>{customer.postcode}</div>}
                  {customer.country && <div style={{ color: '#374151', fontSize: '14px' }}>{customer.country}</div>}
                </>
              ) : (
                <div style={{ color: '#9ca3af', fontSize: '14px' }}>—</div>
              )}
            </div>

            {/* Site */}
            <div>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Site</div>
              {site ? (
                <>
                  <div style={{ fontWeight: 700, fontSize: '16px', color: '#111827' }}>{site.name}</div>
                  {site.street_address && <div style={{ color: '#374151', fontSize: '14px', marginTop: '4px' }}>{site.street_address}</div>}
                  {site.town && <div style={{ color: '#374151', fontSize: '14px' }}>{site.town}</div>}
                  {site.county && <div style={{ color: '#374151', fontSize: '14px' }}>{site.county}</div>}
                  {site.postcode && <div style={{ color: '#374151', fontSize: '14px' }}>{site.postcode}</div>}
                </>
              ) : (
                <div style={{ color: '#9ca3af', fontSize: '14px' }}>—</div>
              )}
            </div>
          </div>

          {/* Job reference */}
          {job && (
            <div style={{ marginBottom: '32px', padding: '16px', background: '#f9fafb', borderRadius: '6px', borderLeft: '3px solid #e5e7eb' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>Job Reference</div>
              <div style={{ fontWeight: 600, fontSize: '15px', color: '#111827' }}>{job.title}</div>
              {job.customer_po && (
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>Customer PO: {job.customer_po}</div>
              )}
            </div>
          )}

          {/* Line items table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '32px' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Description</th>
                <th style={{ textAlign: 'center', padding: '12px 16px', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px', width: '80px' }}>Type</th>
                <th style={{ textAlign: 'right', padding: '12px 16px', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px', width: '120px' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                const val = parseFloat(item.value);
                const isExpense = val < 0;
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '14px 16px', fontSize: '14px', color: '#111827' }}>{item.description}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 10px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: 700,
                        background: isExpense ? '#fee2e2' : '#dbeafe',
                        color: isExpense ? '#b91c1c' : '#1d4ed8',
                      }}>
                        {isExpense ? 'Expense' : 'Line'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: 600, textAlign: 'right', color: isExpense ? '#b91c1c' : '#111827', whiteSpace: 'nowrap' }}>
                      {isExpense ? `(${fmt(Math.abs(val))})` : fmt(val)}
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ padding: '20px 16px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>No line items</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ width: '280px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '2px solid #e5e7eb', fontSize: '16px', fontWeight: 700, color: '#111827' }}>
                <span>Total</span>
                <span>{fmt(quoteTotal)}</span>
              </div>
              {grossProfit !== quoteTotal && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: '14px', color: grossProfit < 0 ? '#b91c1c' : '#374151' }}>
                  <span>Gross Profit</span>
                  <span style={{ fontWeight: 600 }}>{fmt(grossProfit)}</span>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div style={{ background: '#f9fafb', borderTop: '1px solid #e5e7eb', padding: '20px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#9ca3af' }}>Quote #{quote.quote_number}</span>
          <span style={{ fontSize: '12px', color: '#9ca3af' }}>Generated {new Date().toLocaleDateString('en-GB')}</span>
        </div>
      </div>
    </>
  );
};

export default PrintQuote;
